/**
 * quoteDeletion — apagar um orçamento sem deixar fantasma.
 *
 * ─── O BUG QUE ORIGINOU ESTE ARQUIVO (corrupção de dados, achada em device) ──
 *
 * Excluir um orçamento que tinha uma DESPESA COBRÁVEL falhava, e deixava o
 * documento num estado impossível: aprovado, com o total cheio e ZERO itens —
 * um fantasma que entrava nos KPIs do Dashboard e nos relatórios como um
 * orçamento aprovado de valor inteiro sem nenhuma linha.
 *
 * Duas decisões corretas isoladamente se contradiziam no Postgres:
 *
 *   1. `finance_entries.quote_id` é FK **ON DELETE SET NULL** — apagar o
 *      orçamento não pode apagar um gasto que aconteceu de verdade.
 *   2. O CHECK `finance_entries_billable_requires_quote` exige
 *      `quote_id IS NOT NULL` enquanto `billable = true` — uma despesa cobrável
 *      sem documento que a cobre não faz sentido.
 *
 * No `DELETE`, o (1) tenta escrever exatamente a linha que o (2) proíbe, e o
 * comando inteiro é recusado com **23514**. Só que o app já tinha apagado
 * `quote_items` e `quote_photos` (e devolvido o estoque) ANTES de descobrir
 * isso — daí o fantasma.
 *
 * ─── A CORREÇÃO ─────────────────────────────────────────────────────────────
 *
 * A RPC `delete_quotes_cascade` solta a marcação (`billable = false`) e apaga o
 * documento na MESMA transação: ou as duas coisas acontecem, ou nenhuma. O
 * lançamento **sobrevive** como despesa comum, porque o dinheiro foi gasto de
 * verdade — apagá-lo junto (CASCADE) seria destruir um registro financeiro do
 * usuário, e relaxar o CHECK deixaria "cobrável" sem ninguém cobrando.
 *
 * E o cliente **não apaga mais os filhos na mão**: `quote_items`,
 * `quote_photos`, `quote_approvals` e `notifications` já são ON DELETE CASCADE
 * (verificado no Postgres de produção), então os deletes explícitos eram
 * redundantes — e eram justamente eles que abriam a janela de corrupção.
 */
import { supabase } from '@/integrations/supabase/client';
import { plural } from '@/lib/plural';

export interface QuoteDeletionCounts {
  /** Orçamentos efetivamente apagados (a RLS pode recusar algum). */
  deleted: number;
  /** Despesas cobráveis que deixaram de ser cobráveis e seguem no financeiro. */
  released: number;
}

/** Ids reais: um `offline_` nunca chegou ao Postgres. */
function realIds(ids: readonly string[]): string[] {
  return Array.from(
    new Set(
      (ids ?? [])
        .map((id) => String(id ?? ''))
        .filter((id) => id.length > 0 && !id.startsWith('offline_')),
    ),
  );
}

/**
 * Apaga os orçamentos ATOMICAMENTE, soltando antes as despesas cobráveis.
 *
 * Lança em erro — e é importante que lance: quem chama só pode devolver estoque
 * (ou anunciar sucesso) depois que isto voltar sem erro.
 */
export async function deleteQuotesCascade(
  quoteIds: readonly string[],
): Promise<QuoteDeletionCounts> {
  const ids = realIds(quoteIds);
  if (ids.length === 0) return { deleted: 0, released: 0 };

  const { data, error } = await supabase.rpc('delete_quotes_cascade', {
    p_quote_ids: ids,
  });
  if (error) throw error;

  const payload = (data ?? {}) as { deleted?: number; released?: number };
  return {
    deleted: Number(payload.deleted ?? 0),
    released: Number(payload.released ?? 0),
  };
}

/**
 * O aviso de que despesas cobráveis perderam a marcação.
 *
 * Nunca silencioso: o usuário lançou aquilo como cobrável de propósito, e o
 * documento que cobrava acabou de sumir. `null` quando não houve nenhuma — não
 * há por que falar de despesa cobrável a quem não tinha nenhuma.
 */
export function billableReleaseMessage(released: number): string | null {
  if (!Number.isFinite(released) || released <= 0) return null;
  // A frase inteira flexiona, não só o substantivo: "2 despesas ... deixou de
  // ser cobrável" seria o mesmo defeito do #87 uma casa adiante.
  const sujeito = plural(released, 'despesa cobrável', 'despesas cobráveis');
  return released === 1
    ? `${sujeito} deixou de ser cobrável e continua no financeiro como despesa comum.`
    : `${sujeito} deixaram de ser cobráveis e continuam no financeiro como despesas comuns.`;
}
